import Vue from 'vue'
import ability from './ability'
import router from '@/router'
import {
    planHasPermission,
    planHasPermissionAsync,
    isResourceBlockedByPlanOnly,
    fetchPlanPermissionsFromBackend,
    getPlanPermissionsFromStorage
} from './planPermissions'
import { acharResource, listarResources } from './resources'

/**
 * Recursos que são restritos por plano
 * Mapeamento: recurso -> plano mínimo necessário
 * Este mapeamento é usado apenas para exibir o nome do plano na mensagem
 */
const planRestrictedResources = {
    // Exclusivos do Completo Turbo
    'INT_AUTOMACOES': 'Completo Turbo',
    'CONF_CHATBOT': 'Completo Turbo',
    'CONF_SITE_PROPRIO': 'Completo Turbo',
    'CONF_GOOGLE_MEU_NEGOCIO': 'Completo Turbo',

    // Exclusivos do Completo
    'REL_CONTASAPAGAR': 'Completo',
    'CAD_MAQUININHAS': 'Completo',
    'CONF_CATALOGO_VIRTUAL': 'Completo',
    'INT_INTEGRACOES': 'Completo',
    'CAD_FIDELIDADE': 'Completo',
    'NFE_ENTRADA': 'Completo',
    'CAD_PATRIMONIO': 'Completo',
    'CAD_COMISSAO': 'Completo',
    'REL_COMISSAO': 'Completo',
    'CAD_FORNECEDOR': 'Completo',
    'REL_PATRIMONIO': 'Completo',
    'REL_DRE': 'Completo',
    'CAD_CONTABANCARIA': 'Completo',

    // Intermediário ou Completo
    'TAR_IMPORTACAO': 'Intermediário',
    'TAR_NOTAFISCAL': 'Intermediário',
    'ACT_ENVIARSMS': 'Intermediário',
}

/**
 * Nomes amigáveis dos recursos para exibir no swal
 */
const resourceNames = {
    'REL_CONTASAPAGAR': 'Contas a Pagar',
    'CAD_MAQUININHAS': 'Cadastro de Maquininhas',
    'CONF_CATALOGO_VIRTUAL': 'Catálogo Virtual',
    'INT_INTEGRACOES': 'Integrações e Disparo de Webhook',
    'INT_AUTOMACOES': 'Automatização de disparos de WhatsApp',
    'CONF_CHATBOT': 'ChatBot 24h no WhatsApp',
    'CONF_SITE_PROPRIO': 'Criação de Site Próprio',
    'CONF_GOOGLE_MEU_NEGOCIO': 'Otimização Google Meu Negócio',
    'CAD_FIDELIDADE': 'Programa de Fidelidade',
    'TAR_IMPORTACAO': 'Importação de Dados',
    'TAR_NOTAFISCAL': 'Emissão de NFS-e',
    'ACT_ENVIARSMS': 'Envio de SMS',
    'NFE_ENTRADA': 'NFe de Entrada (Manifesto + Importação de Produtos via Nota Fiscal)',
    'CAD_PATRIMONIO': 'Gestão de Patrimônio',
    'REL_PATRIMONIO': 'Relatório de Patrimônio',
    'CAD_COMISSAO': 'Controle de Comissionamento',
    'REL_COMISSAO': 'Relatório de Comissões',
    'CAD_FORNECEDOR': 'Cadastro de Fornecedores',
    'REL_DRE': 'DRE - Demonstração do Resultado',
    'CAD_CONTABANCARIA': 'Cadastro de Contas Bancárias',
}

/**
 * Verifica se um recurso está na lista de recursos que podem ser restritos por plano
 * @param {string} resource - Nome do recurso
 * @returns {boolean}
 */
export const isPlanRestricted = (resource) => {
    // `resource` pode ser 'A|B' (OR): basta um dos lados ser restrito por plano.
    return listarResources(resource)
        .some((r) => Object.prototype.hasOwnProperty.call(planRestrictedResources, r))
}

/**
 * Verifica se o PLANO ATUAL tem permissão para um recurso
 * Usa o localStorage para verificação rápida
 * @param {string} resource - Nome do recurso
 * @returns {boolean}
 */
export const planHasResourcePermission = (resource) => {
    const resources = listarResources(resource)
    if (!resources.length) return planHasPermission(resource)

    return resources.some((r) => planHasPermission(r))
}

/**
 * Verifica se o PLANO ATUAL tem permissão para um recurso (versão assíncrona com validação backend)
 * @param {string} resource - Nome do recurso
 * @returns {Promise<boolean>}
 */
export const planHasResourcePermissionAsync = async (resource) => {
    const resources = listarResources(resource)
    if (!resources.length) return planHasPermissionAsync(resource)

    for (const r of resources) {
        // eslint-disable-next-line no-await-in-loop
        if (await planHasPermissionAsync(r)) return true
    }

    return false
}

/**
 * Retorna o plano mínimo necessário para acessar um recurso
 * @param {string} resource - Nome do recurso
 * @returns {string|null}
 */
export const getRequiredPlan = (resource) => {
    const restrito = acharResource(resource, (r) => !!planRestrictedResources[r])

    return restrito ? planRestrictedResources[restrito] : null
}

/**
 * Retorna o nome amigável de um recurso
 * @param {string} resource - Nome do recurso
 * @returns {string}
 */
export const getResourceName = (resource) => {
    const conhecido = acharResource(resource, (r) => !!resourceNames[r])
    if (conhecido) return resourceNames[conhecido]

    // Sem nome amigável: nunca devolver 'A|B' cru para a tela — usa o primeiro
    // resource restrito por plano (é dele que a mensagem de upgrade fala) ou,
    // na falta dele, o primeiro da lista.
    const restrito = acharResource(resource, (r) => !!planRestrictedResources[r])

    return restrito || listarResources(resource)[0] || resource
}

/**
 * Verifica se o usuário tem acesso a um recurso considerando o plano
 * @param {string} action - Ação (read, create, update, delete)
 * @param {string} resource - Nome do recurso
 * @returns {boolean}
 */
export const canAccessResource = (action, resource) => {
    const resources = listarResources(resource)
    if (!resources.length) return ability.can(action, resource)

    return resources.some((r) => ability.can(action, r))
}

/**
 * Verifica se a falta de permissão é por restrição de plano (e não por perfil)
 * IMPORTANTE: Esta função diferencia entre:
 * - Bloqueio por PLANO: o plano da empresa não tem a permissão
 * - Bloqueio por PERFIL: o plano tem, mas o perfil do usuário não
 * @param {string} action - Ação
 * @param {string} resource - Recurso
 * @returns {boolean}
 */
export const isBlockedByPlan = (action, resource) => {
    // Se o usuário tem a permissão, não está bloqueado
    if (canAccessResource(action, resource)) {
        return false
    }

    // Verifica se o recurso está na lista de recursos que podem ser restritos
    if (!isPlanRestricted(resource)) {
        return false
    }

    // Verifica se o PLANO tem a permissão usando o localStorage
    // Se o plano não tem a permissão, é bloqueio de plano
    // Se o plano tem a permissão, é bloqueio de perfil (não mostra mensagem de upgrade)
    return !planHasResourcePermission(resource)
}

/**
 * Verifica se a falta de permissão é por restrição de plano (versão assíncrona com dupla validação)
 * @param {string} action - Ação
 * @param {string} resource - Recurso
 * @returns {Promise<boolean>}
 */
export const isBlockedByPlanAsync = async (action, resource) => {
    // Se o usuário tem a permissão, não está bloqueado
    if (canAccessResource(action, resource)) {
        return false
    }

    // Verifica se o recurso está na lista de recursos que podem ser restritos
    if (!isPlanRestricted(resource)) {
        return false
    }

    // Dupla validação: busca no backend para ter certeza
    const hasPlanPermission = await planHasResourcePermissionAsync(resource)
    return !hasPlanPermission
}

/**
 * Exibe o SweetAlert de upgrade de plano
 * IMPORTANTE: Esta função faz dupla validação no backend antes de exibir
 * @param {string} resource - Nome do recurso bloqueado
 * @param {object} vm - Instância Vue para acessar $swal
 * @param {boolean} skipValidation - Pular validação (usar quando já foi validado)
 */
export const showPlanUpgradeAlert = async (resource, vm, skipValidation = false) => {
    const swalInstance = vm.$swal || Vue.prototype.$swal

    if (!swalInstance) {
        console.error('SweetAlert não está disponível')
        return
    }

    // Dupla validação: verifica no backend se realmente é bloqueio de plano
    if (!skipValidation) {
        const isReallyBlockedByPlan = await isBlockedByPlanAsync('read', resource)
        if (!isReallyBlockedByPlan) {
            // Não é bloqueio de plano, provavelmente é bloqueio de perfil
            // Não mostra a mensagem de upgrade
            return false
        }
    }

    const requiredPlan = getRequiredPlan(resource) || 'superior'
    const resourceName = getResourceName(resource)

    swalInstance({
        title: 'Funcionalidade não disponível',
        html: `
            <div class="text-center">
                <p class="mb-1">A funcionalidade <strong>"${resourceName}"</strong> não está disponível no seu plano atual.</p>
                <p class="text-muted mb-0">Faça upgrade para o plano <strong>${requiredPlan}</strong> ou superior para desbloquear este recurso.</p>
            </div>
        `,
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Ver Planos',
        cancelButtonText: 'Fechar',
        customClass: {
            confirmButton: 'btn btn-primary mr-2',
            cancelButton: 'btn btn-outline-secondary',
        },
        buttonsStyling: false,
    }).then((result) => {
        if (result.isConfirmed) {
            router.push({ name: 'planosepagamentos' })
        }
    })

    return true
}

/**
 * Verifica permissão e mostra swal se bloqueado por plano
 * Retorna true se pode acessar, false se foi bloqueado
 * IMPORTANTE: Esta função diferencia bloqueio de plano vs perfil
 * @param {string} action - Ação
 * @param {string} resource - Recurso
 * @param {object} vm - Instância Vue
 * @returns {Promise<boolean>}
 */
export const checkPermissionOrShowUpgrade = async (action, resource, vm) => {
    if (canAccessResource(action, resource)) {
        return true
    }

    // Verifica se o recurso está na lista de recursos que podem ser restritos
    if (!isPlanRestricted(resource)) {
        // Não é um recurso restrito por plano, é bloqueio de perfil
        return false
    }

    // Verifica se é realmente bloqueio de plano (não de perfil)
    // Usa validação assíncrona para dupla checagem
    const isReallyPlanBlocked = await isBlockedByPlanAsync(action, resource)

    if (isReallyPlanBlocked) {
        // É bloqueio de plano, mostra modal de upgrade
        await showPlanUpgradeAlert(resource, vm, true) // skipValidation = true pois já validamos
        return false
    }

    // É bloqueio de perfil (plano tem, mas usuário não), não mostra modal de upgrade
    return false
}

/**
 * Mixin Vue para usar nos componentes
 */
export const planUpgradeMixin = {
    methods: {
        async $canOrUpgrade(action, resource) {
            return await checkPermissionOrShowUpgrade(action, resource, this)
        },
        async $showPlanUpgrade(resource) {
            return await showPlanUpgradeAlert(resource, this)
        },
        $isPlanRestricted(resource) {
            return isPlanRestricted(resource)
        },
        $isBlockedByPlan(action, resource) {
            return isBlockedByPlan(action, resource)
        },
        async $isBlockedByPlanAsync(action, resource) {
            return await isBlockedByPlanAsync(action, resource)
        },
        $planHasPermission(resource) {
            return planHasResourcePermission(resource)
        },
    },
}

export default {
    isPlanRestricted,
    getRequiredPlan,
    getResourceName,
    canAccessResource,
    isBlockedByPlan,
    isBlockedByPlanAsync,
    showPlanUpgradeAlert,
    checkPermissionOrShowUpgrade,
    planUpgradeMixin,
    planHasResourcePermission,
    planHasResourcePermissionAsync,
}
